import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { AppConfigsService } from '../../../config/app-configs.service';
import { HttpService, IResponseLower } from '../../../shared/cardholders-core/services/http-service.service';
import { UiHelperService } from '../../../shared/cardholders-core/services/ui-helper.service';
import { IBenefitsLobby } from '../interfaces/category-data.interface';
import { ICategoryLobbyResult, ISmartairBenefitsWithLocations } from '../interfaces/categories-lobby.interface';
import { IGetBenefitByNameResponse } from '../interfaces/benefit-response.interface';
import { IBenefitErrorScreen } from '../interfaces/ecom-settings.interface';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { tap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { AuthService } from '../../../shared/cardholders-core/services/auth.service';
import { SeoService } from 'core-max-lib';

@Injectable({
    providedIn: 'root'
})
export class BenefitsService {
    loaderStatus = new BehaviorSubject<boolean>(false);
    loaderCounter = 0;
    benefitCategoryFaqCount: number = 0;
    benefitdata: IGetBenefitByNameResponse;
    campaignBenefitToPurchase = null;
    purchaseResultScreen: IBenefitErrorScreen;
    constructor(
        private appConfigsService: AppConfigsService,
        private httpSvc: HttpService,
        @Inject(DOCUMENT) private readonly document: Document,
        private uiHelper: UiHelperService,
        private authService: AuthService,
        private seoService: SeoService
    ) {}

    set loader(add: boolean) {
        if (add) {
            this.loaderCounter += 1;
            this.loaderStatus.next(true);
        } else {
            this.loaderCounter -= 1;
            if (this.loaderCounter < 0) {
                this.loaderCounter = 0;
            }
            if (this.loaderCounter === 0) {
                this.loaderStatus.next(false);
            }
        }
    }

    getLobby(): Observable<IResponseLower<IBenefitsLobby>> {
        let url =
            this.appConfigsService.appConfigs.apiUrl +
            (this.authService.isUserAuthenticated() ? '/registered' : '') +
            '/benefits/getLobby';
        return this.httpSvc.getLower<IBenefitsLobby>(url);
    }

    getCategoriesLobby(
        isMobile: boolean,
        page: number,
        loadLobbyTemplate: boolean,
        categoryUrlName,
        clubName,
        regionId
    ): Observable<IResponseLower<ICategoryLobbyResult>> {
        if(this.uiHelper.isBrowser){
            this.loader = true;
            let url = `${this.appConfigsService.appConfigs.apiUrl}${
                this.authService.isUserAuthenticated() ? '/registered' : ''
            }/benefits/getCategoriesLobby?isMobile=${isMobile}&page=${page}&loadLobby=${loadLobbyTemplate}&category=${encodeURIComponent(
                categoryUrlName
            )}&club=${clubName}&region=${regionId}`;
            return this.httpSvc.getLower<ICategoryLobbyResult>(url).pipe(
                tap((_) => {
                    this.loader = false;
                })
            );
        }
    }

    getSmartairBenefitsWithLocations(): Observable<IResponseLower<ISmartairBenefitsWithLocations>> {
        let url = `${this.appConfigsService.appConfigs.apiUrl}${
            this.authService.isUserAuthenticated() ? '/registered' : ''
        }/benefits/getSmartairBenefits`;
        return this.httpSvc.getLower<ISmartairBenefitsWithLocations>(url);
    }

    purchaseCampaignBenefit(id: number): Observable<IResponseLower<IBenefitErrorScreen>> {
        return this.httpSvc.postLower<IBenefitErrorScreen>(`${this.appConfigsService.appConfigs.apiUrl}/registered/benefits/purchaseCampaignBenefit`,
        { id });
    }

    getSSOData(): Observable<IResponseLower<string>> {
        return this.httpSvc.getLower<string>(`${this.appConfigsService.appConfigs.apiUrl}/benefits/getSSOData`);
    }

    insertStylesFix(color: string): void {
        if (this.document.querySelector(`style[id="dictionaryStyleFixes"]`) || !color) {
            return;
        }
        const s = this.document.createElement('style');
        // tslint:disable-next-line: deprecation
        s.type = 'text/css';
        s.id = 'dictionaryStyleFixes';
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" fill-rule="evenodd"><g fill="${color}" fill-rule="nonzero"><g><path d="M11.267 11.74l3.114 3.109c.304.303.304.794 0 1.098-.303.303-.795.303-1.099 0l-3.663-3.66c-.304-.302-.304-.794 0-1.097l3.663-3.658c.304-.303.796-.303 1.1 0 .303.303.303.794 0 1.097l-3.115 3.11z" transform="translate(-115 -2315) translate(115 2315)"/></g></g></g></svg>`;
        const encodedSvg = encodeURIComponent(svg);
        s.innerHTML =
            `.arrow-link:after {background: url("data:image/svg+xml,` +
            encodedSvg +
            `") !important;}
            .arrow-link:hover:after {filter: none !important;}`;
        this.document.head.appendChild(s);
    }

    getBenefitByName(category: string, name: string): Observable<IResponseLower<IGetBenefitByNameResponse>> {
        this.loader = true;
        const apiRoute = name.startsWith('smartair') ? 'getSmartairBenefitByName' : 'getBenefitByName';
        let url =
            this.appConfigsService.appConfigs.apiUrl +
            (this.authService.isUserAuthenticated() ? '/registered' : '') +
            `/benefits/${apiRoute}?name=${name}&category=${category}`;
        return this.httpSvc.getLower<IGetBenefitByNameResponse>(url).pipe(
            tap((_) => {
                this.loader = false;
            })
        );
    }

    removeStylesFix(): void {
        if (this.uiHelper.isBrowser) {
            const element = this.document.querySelector(`style[id="dictionaryStyleFixes"]`);
            if (element) {
                element.remove();
            }
        }
    }

    insertBodyStylesFix(): void {
        if (this.document.querySelector(`style[id="bodyStyleFixes"]`)) {
            return;
        }
        const s = this.document.createElement('style');
        s.type = 'text/css';
        s.id = 'bodyStyleFixes';
        s.innerHTML = `body {
                overflow: hidden !important;
            }`;
        this.document.head.appendChild(s);
    }

    removeBodyStylesFix(): void {
        if (this.uiHelper.isBrowser) {
            const element = this.document.querySelector(`style[id="bodyStyleFixes"]`);
            if (element) {
                element.remove();
            }
        }
    }

    updateFaq(questionsAndAnswers) {
        let faqScript = this.document.querySelector(`script[id="faqScript"]`);
        if (!faqScript) {
            if (questionsAndAnswers) {
                this.seoService.setQuestionAndAnswers(
                    questionsAndAnswers.map((faq) => {
                        this.benefitCategoryFaqCount++;
                        return { QuestionText: faq.title, AnswerText: faq.body };
                    })
                );
            }
            return;
        }
        let data = JSON.parse(faqScript.innerHTML);
        if (this.benefitCategoryFaqCount > 0) {
            data['mainEntity'] = (data['mainEntity'] as Array<any>).slice(0, this.benefitCategoryFaqCount * -1);
            this.benefitCategoryFaqCount = 0;
        }
        let newFaq = questionsAndAnswers?.map((qa) => this.buildItemForFaq(qa.title, qa.body));
        newFaq?.forEach((faq) => {
            (data['mainEntity'] as Array<any>).push(faq);
            this.benefitCategoryFaqCount++;
        });
        this.document.querySelector(`script[id="faqScript"]`).innerHTML = JSON.stringify(data);
    }

    private buildItemForFaq(q, a) {
        let item = {
            '@type': 'Question',
            name: null,
            acceptedAnswer: {
                '@type': 'Answer',
                text: null
            }
        };
        item['name'] = q;
        item['acceptedAnswer']['text'] = a;
        return item;
    }
}
